export const RESOURCE_LABELS = {
    REPORT: "Relatórios",
    PROVIDER: "Fornecedor",
    CLIENT_COMPANY: "Empresas Compradoras",
    USER_SETTINGS: "Configurações do Usuário",
    ACCOUNT_SETTINGS: "Configurações da Conta",
    ATTENDANCE: "Atendimento",
    CLIENT_EXTERNAL: "Clientes Externos",
    CLIENT_SELLER: "Cliente - Vendedor",
    INVITATION: "Convites",
    MAIN_TOP: "Topo Principal",
    MY_QUOTE: "Minhas Cotações",
    MY_COMPANY: "Minha Empresa",
    PROFILE: "Perfis",
    RESALE: "Revendas",
    SELLER_CONDITION: "Condições do Vendedor",
    SYSCONF: "Configurações do Sistema",
    USER: "Usuário",
    PRODUCT: "Produtos",
    REQUEST_ORDER: "Pedidos",
    DIRECT_PURCHASE: "Venda Direta",
    DIRECT_SHOPPING_CREDIT: "Créditos Compra Direta",
    REQUEST: "Cotações",
    REQUEST_PRODUCT: "Itens da Cotação",
    CAMPAIGN: "Campanhas",
    SELLER: "Vendedores",
    WEBHOOK: "Webhooks",
    SELLER_CLIENT: "Vendedor - Cliente",
    NOTIFICATION_SETTINGS: "Notificações",
    ADMIN: "Administração",
    MAP: "Mapas",
};

export const ACTION_LABELS = {
    READ: "Leitura",
    READ_ALL: "Leitura Total",
    READ_OTHERS: "Leitura Completa",
    READ_EXTERNAL: "Leitura Externa",
    READ_FROM_SUPERVISEE: "Leitura Supervisor",
    READ_FROM_PROVIDER: "Leitura do Fornecedor",
    READ_HISTORY: "Histórico",
    CREATE: "Criar",
    UPDATE: "Atualizar",
    DELETE: "Remover",
    ADD: "Adicionar",
    ADD_OTHERS: "Adicionar em Qualquer Cliente",
    ADD_PRICE: "Adicionar Preço",
    ADD_SELLER: "Adicionar Vendedor",
    MANAGE_GROUPS: "Gerenciar Grupos",
    ACTIVATE_DEACTIVATE: "Ativar/Desativar",
    UPDATE_PROFILE: "Atualizar Perfil",
    UPDATE_GROUP: "Atualizar Grupo",
    UPDATE_ALL: "Atualizar Tudo",
    DIRECT_ORDER_ONLY: "Somente Pedido Direto",
    PRICING: "Níveis de Preço",
    NEW_COMPANY: "Nova Empresa",
    ACCESS: "Acesso",
    MANAGE: "Gerenciar",
    CREATE_RESUPPLY: "Criar Ressuprimento",
    DELETE_RESUPPLY: "Remover Ressuprimento",
    READ_SELLERS: "Visualizar Vendedores",
    SEND_NOTIFICATION: "Reenviar Notificação",
    MANAGE_OFFER: "Editar Preços",
    SEE_REPORT: "Relatório",
    READ_ALL_SUBREQUESTS: "Visualizar Pré-Pedidos",
    READ_PROVIDERS_MAP: "Visualizar Mapa",
};

export const VISIBILITY_OPTIONS = [
    { value: "all", label: "Todos" },
    { value: "provider", label: "Fornecedor" },
    { value: "client", label: "Cliente" },
    { value: "admin", label: "Admin" },
];

const ACTION_PRIORITY = {
    READ: 0,
    ACCESS: 1,
    MANAGE: 2,
    UPDATE: 3,
    CREATE: 4,
    ADD: 5,
    DELETE: 6,
};

export function extractCollectionFromPayload(payload) {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (payload && payload.data !== undefined) {
        return extractCollectionFromPayload(payload.data);
    }

    return [];
}

export function extractItemFromPayload(payload) {
    if (Array.isArray(payload)) {
        return payload[0] || null;
    }

    if (payload && payload.data !== undefined) {
        return extractItemFromPayload(payload.data);
    }

    return payload || null;
}

export function normalizePermissionRecord(permission = {}) {
    return {
        ...permission,
        resource: permission.resource || "",
        action: permission.action || "",
        name: permission.name || "",
        description: permission.description || "",
        visibility: permission.visibility || "all",
    };
}

export function translatePermissionResource(resource) {
    return RESOURCE_LABELS[resource] || resource;
}

export function translatePermissionAction(action) {
    return ACTION_LABELS[action] || action;
}

export function getPermissionKey(permission = {}) {
    return `${permission.resource || ""}::${permission.action || ""}`;
}

export function buildPermissionLookup(permissions = []) {
    return new Set(permissions.map((permission) => getPermissionKey(permission)));
}

export function filterPermissionsByVisibility(permissions = [], visibility) {
    if (!visibility) {
        return permissions.map((permission) => normalizePermissionRecord(permission));
    }

    return permissions
        .map((permission) => normalizePermissionRecord(permission))
        .filter((permission) => {
            const permissionVisibility = permission.visibility || "all";
            return permissionVisibility === "all" || permissionVisibility === visibility;
        });
}

function getActionPriority(action) {
    return ACTION_PRIORITY[action] != null ? ACTION_PRIORITY[action] : Number.MAX_SAFE_INTEGER;
}

function sortPermissions(left, right) {
    const actionDiff = getActionPriority(left.action) - getActionPriority(right.action);

    if (actionDiff !== 0) {
        return actionDiff;
    }

    const leftLabel = translatePermissionAction(left.action) || left.name || "";
    const rightLabel = translatePermissionAction(right.action) || right.name || "";
    const labelDiff = leftLabel.localeCompare(rightLabel);

    if (labelDiff !== 0) {
        return labelDiff;
    }

    return (left.name || "").localeCompare(right.name || "");
}

function getPrimaryPermission(permissions) {
    return (
        permissions.find((permission) => permission.action === "READ") ||
        permissions.find((permission) => permission.action === "ACCESS") ||
        permissions.find((permission) => permission.action === "MANAGE") ||
        permissions.find((permission) => permission.action === "UPDATE") ||
        permissions.find((permission) => permission.action === "CREATE") ||
        permissions[0] ||
        null
    );
}

export function buildPermissionModules(permissions = []) {
    const groupedPermissions = permissions.reduce((carry, permission) => {
        const normalizedPermission = normalizePermissionRecord(permission);

        if (!normalizedPermission.resource || !normalizedPermission.action) {
            return carry;
        }

        if (!carry[normalizedPermission.resource]) {
            carry[normalizedPermission.resource] = [];
        }

        carry[normalizedPermission.resource].push(normalizedPermission);

        return carry;
    }, {});

    return Object.keys(groupedPermissions)
        .map((resource) => {
            const modulePermissions = groupedPermissions[resource].slice().sort(sortPermissions);
            const primaryPermission = getPrimaryPermission(modulePermissions) || { resource, action: "" };

            return {
                ...primaryPermission,
                name: translatePermissionResource(resource),
                permissions: modulePermissions,
            };
        })
        .sort((left, right) => translatePermissionResource(left.resource).localeCompare(translatePermissionResource(right.resource)));
}
